import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Mail, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailVerificationModal = ({ isOpen, onClose, onVerified }) => {
  const { t, i18n } = useTranslation();
  const { token, user, setUser } = useAuth();
  const [step, setStep] = useState('prompt'); // 'prompt' | 'code' | 'success'
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  if (!isOpen) return null;

  const handleSendCode = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/api/auth/verify-account`, 
        { action: 'send' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep('code');
      toast.success(t('auth.verificationSent', 'Verification code sent to your email'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      toast.error(t('auth.enterValidCode', 'Enter a valid 6-digit code'));
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API}/api/auth/verify-account`,
        { action: 'verify', code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep('success');
      if (setUser && user) {
        setUser({ ...user, emailVerified: true });
      }
      toast.success(t('auth.emailVerified', 'Email verified successfully'));
      setTimeout(() => {
        onVerified?.();
        onClose();
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="email-verification-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative">
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          data-testid="close-verification-modal"
        >
          <X className="h-5 w-5" />
        </button>

        {step === 'prompt' && (
          <div className="text-center space-y-4">
            <div className="mx-auto w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <Mail className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('auth.verifyYourEmail', 'Verify Your Email')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('auth.verifyToPerformAction', 'To use this feature, please verify your email address. We\'ll send a quick 6-digit code to:')}
            </p>
            <p className="font-medium text-slate-800 dark:text-slate-200">
              {user?.email}
            </p>
            <Button 
              onClick={handleSendCode} 
              disabled={sending}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-verify-code-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('auth.sendCode', 'Send Verification Code')}
            </Button>
          </div>
        )}

        {step === 'code' && (
          <div className="text-center space-y-4">
            <div className="mx-auto w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <Mail className="h-7 w-7 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('auth.enterVerificationCode', 'Enter Verification Code')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('auth.codeSentTo', 'We sent a 6-digit code to')} <strong>{user?.email}</strong>
            </p>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono"
              autoFocus
              data-testid="modal-verification-code-input"
            />
            <div className="flex gap-2">
              <Button 
                onClick={handleVerifyCode} 
                disabled={verifying || code.length !== 6}
                className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="modal-verify-btn"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('auth.verify', 'Verify')}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSendCode} 
                disabled={sending}
                className="rounded-full"
                data-testid="modal-resend-btn"
              >
                {t('auth.resend', 'Resend')}
              </Button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="text-center space-y-4 py-4">
            <div className="mx-auto w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
              {t('auth.emailVerified', 'Email Verified')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('auth.youCanNowProceed', 'You can now use all features. Continuing...')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationModal;
